import cn from 'classnames';
import { forwardRef, useMemo } from 'react';

import { BaseField } from '../base-field';
import { FieldLabel } from '../field-label';

import { CheckboxGroupContext } from './checkbox-group-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { CheckboxGroupProps } from './checkbox-group.types';

import './checkbox-group.scss';

/**
 * The `CheckboxGroup` component is used to group multiple `Checkbox` or `CheckboxField` components together.
 * It provides a way to manage the state of multiple checkboxes as a single entity.
 * The `CheckboxGroup` component can be used to group checkboxes in a horizontal or vertical layout.

 * ### Usage
 *
 * ```tsx
 * import { CheckboxGroup, CheckboxField } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCheckboxFieldComponent() {
 *  return (
 *  <CheckboxGroup label="Select your favourite colors">
 *      <CheckboxField label="White" />
 *      <CheckboxField label="Black" />
 *      <CheckboxField label="Red" />
 *  </CheckboxGroup>
 *  )};
 * ```
 *
 * ### Usage
 *
 * ```tsx
 * import { CheckboxGroup, Checkbox } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCheckboxComponent() {
 * return (
 * <CheckboxGroup label="Select one or more checkboxes">
 *     <Checkbox />
 *     <Checkbox />
 *     <Checkbox />
 * <CheckboxGroup>
 * )};
 * ```
 */
const CheckboxGroup = forwardRef(
  (props: CheckboxGroupProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      disabled = false,
      label,
      orientation = 'vertical',
      readOnly = false,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const contextProviderValue = useMemo(
      () => ({
        disabled,
        readOnly,
      }),
      [disabled, readOnly],
    );

    const fieldLabel = label && <FieldLabel>{label}</FieldLabel>;

    return (
      <CheckboxGroupContext.Provider value={contextProviderValue}>
        <BaseField
          className={cn('rbui-checkbox-group', `rbui-checkbox-group--${orientation}`, className)}
          control={
            <div className={cn('rbui-checkbox-group__list')} role="group">
              {children}
            </div>
          }
          label={fieldLabel}
          labelPosition="block-start"
          ref={forwardedRef}
          {...rest}
        />
      </CheckboxGroupContext.Provider>
    );
  },
);

CheckboxGroup.displayName = 'CheckboxGroup';

export default CheckboxGroup;
